/**
 * Scroll management hook for chat interfaces
 * Follows ADR-003: High Cohesion - Single responsibility: manage scroll behavior
 */

import { useRef, useCallback, useEffect } from 'react';
import { isLayoutDebugEnabled, noteLayoutDebugEvent } from '../utils/layoutDebug';

/** Content-resize deltas above this use native smooth scroll while pinned. */
const RESIZE_SMOOTH_DELTA_THRESHOLD = 8;

/** How long to treat a smooth-scroll target as in-flight before allowing a restart. */
const SMOOTH_SCROLL_PENDING_MS = 300;

/**
 * Options for useScrollManagement hook
 */
export interface UseScrollManagementOptions {
  /** Whether to automatically scroll to new messages (default: true) */
  scrollToNewMessage?: boolean;
  /** Whether to respect user scroll position (default: true) */
  respectUserScroll?: boolean;
  /** Threshold in pixels to consider "near bottom" / pinned */
  scrollThreshold?: number;
}

/**
 * Return value for useScrollManagement hook
 */
export interface UseScrollManagementReturn {
  /** Ref for the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Ref for the scrollable content wrapper (ResizeObserver target) */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Ref for the scroll end marker */
  endRef: React.RefObject<HTMLDivElement | null>;
  /** Handle scroll events */
  handleScroll: () => void;
  /** Scroll to bottom (optionally force regardless of user position) */
  scrollToBottom: (force?: boolean) => void;
  /** Scroll if there are new messages */
  scrollToBottomIfNewMessages: (messageCount: number) => void;
  /** Scroll when content height changes (streaming growth) */
  scrollToBottomOnContentResize: () => void;
}

/**
 * Custom hook for managing scroll behavior in chat interfaces.
 * Supports pinned-to-bottom auto-scroll that respects manual user scrolling.
 */
export function useScrollManagement({
  scrollToNewMessage = true,
  respectUserScroll = true,
  scrollThreshold = 150,
}: UseScrollManagementOptions = {}): UseScrollManagementReturn {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const isPinnedToBottomRef = useRef<boolean>(true);
  const previousMessageCountRef = useRef<number>(0);
  const isProgrammaticScrollRef = useRef<boolean>(false);
  const resizeScrollRafRef = useRef<number | null>(null);
  const pendingSmoothTargetRef = useRef<number | null>(null);
  const pendingSmoothClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingSmoothScroll = useCallback((): void => {
    if (pendingSmoothClearTimerRef.current !== null) {
      clearTimeout(pendingSmoothClearTimerRef.current);
      pendingSmoothClearTimerRef.current = null;
    }
    pendingSmoothTargetRef.current = null;
  }, []);

  const schedulePendingSmoothScroll = useCallback((target: number): void => {
    pendingSmoothTargetRef.current = target;
    if (pendingSmoothClearTimerRef.current !== null) {
      clearTimeout(pendingSmoothClearTimerRef.current);
    }
    pendingSmoothClearTimerRef.current = setTimeout(() => {
      pendingSmoothTargetRef.current = null;
      pendingSmoothClearTimerRef.current = null;
    }, SMOOTH_SCROLL_PENDING_MS);
  }, []);

  const getMaxScrollTop = useCallback((container: HTMLElement): number => {
    return Math.max(0, container.scrollHeight - container.clientHeight);
  }, []);

  /**
   * Checks if the viewport is pinned to (or near) the bottom
   */
  const checkIfPinnedToBottom = useCallback((): boolean => {
    const container = containerRef.current;
    if (!container) return true;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom <= scrollThreshold;
  }, [scrollThreshold]);

  /**
   * Applies scroll position without a long custom animation loop.
   * Streaming/content growth uses instant updates so user wheel/touch is not fought.
   */
  const applyScrollToBottom = useCallback(
    (options: { force?: boolean; smooth?: boolean } = {}): void => {
      const { force = false, smooth = false } = options;
      if (!scrollToNewMessage) return;

      const container = containerRef.current;
      if (!container) return;

      if (respectUserScroll && !force && !isPinnedToBottomRef.current) {
        return;
      }

      const target = getMaxScrollTop(container);
      if (!force && Math.abs(target - container.scrollTop) <= 1) {
        return;
      }

      if (smooth && !force && pendingSmoothTargetRef.current === target) {
        return;
      }

      isProgrammaticScrollRef.current = true;

      const previousScrollTop = container.scrollTop;

      if (smooth && !force && typeof container.scrollTo === 'function') {
        container.scrollTo({ top: target, behavior: 'smooth' });
        schedulePendingSmoothScroll(target);
      } else {
        clearPendingSmoothScroll();
        container.scrollTop = target;
      }

      if (isLayoutDebugEnabled()) {
        noteLayoutDebugEvent('scroll-apply', {
          force,
          smooth,
          previousScrollTop,
          target,
          nextScrollTop: container.scrollTop,
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          pinned: isPinnedToBottomRef.current,
        });
      }

      isPinnedToBottomRef.current = true;

      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    },
    [scrollToNewMessage, respectUserScroll, getMaxScrollTop, schedulePendingSmoothScroll, clearPendingSmoothScroll]
  );

  /**
   * Handles scroll events to track pinned state
   */
  const handleScroll = useCallback((): void => {
    if (isProgrammaticScrollRef.current) return;
    if (respectUserScroll) {
      isPinnedToBottomRef.current = checkIfPinnedToBottom();
    }

    if (isLayoutDebugEnabled()) {
      const container = containerRef.current;
      if (container) {
        noteLayoutDebugEvent('user-scroll', {
          scrollTop: container.scrollTop,
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          pinned: isPinnedToBottomRef.current,
        });
      }
    }
  }, [respectUserScroll, checkIfPinnedToBottom]);

  const scrollToBottom = useCallback(
    (force: boolean = false): void => {
      applyScrollToBottom({ force, smooth: force });
    },
    [applyScrollToBottom]
  );

  const scrollToBottomOnContentResize = useCallback((): void => {
    if (resizeScrollRafRef.current !== null) return;

    resizeScrollRafRef.current = requestAnimationFrame(() => {
      resizeScrollRafRef.current = null;

      const container = containerRef.current;
      if (!container) return;

      if (respectUserScroll && !isPinnedToBottomRef.current) {
        return;
      }

      const target = getMaxScrollTop(container);
      if (Math.abs(target - container.scrollTop) <= 1) {
        return;
      }

      if (isLayoutDebugEnabled()) {
        noteLayoutDebugEvent('resize-scroll', {
          scrollTop: container.scrollTop,
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          pinned: isPinnedToBottomRef.current,
          target,
        });
      }
      const delta = target - container.scrollTop;
      applyScrollToBottom({ force: false, smooth: delta > RESIZE_SMOOTH_DELTA_THRESHOLD });
    });
  }, [applyScrollToBottom, respectUserScroll, getMaxScrollTop]);

  const scrollToBottomIfNewMessages = useCallback(
    (messageCount: number): void => {
      if (!scrollToNewMessage) return;

      const hasNewMessages = messageCount > previousMessageCountRef.current;

      if (hasNewMessages) {
        previousMessageCountRef.current = messageCount;
        requestAnimationFrame(() => {
          scrollToBottom(false);
        });
      }
    },
    [scrollToNewMessage, scrollToBottom]
  );

  // ResizeObserver: keep pinned view following streaming growth
  useEffect(() => {
    const content = contentRef.current;
    const container = containerRef.current;
    if (!content || !container) return;

    const observer = new ResizeObserver(() => {
      scrollToBottomOnContentResize();
    });

    observer.observe(content);
    return () => {
      if (resizeScrollRafRef.current !== null) {
        cancelAnimationFrame(resizeScrollRafRef.current);
        resizeScrollRafRef.current = null;
      }
      clearPendingSmoothScroll();
      observer.disconnect();
    };
  }, [scrollToBottomOnContentResize, clearPendingSmoothScroll]);

  // Cancel auto-scroll intent when the user interacts manually
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !respectUserScroll) return;

    const onWheel = (event: WheelEvent): void => {
      if (isProgrammaticScrollRef.current) return;
      if (event.deltaY < 0) {
        isPinnedToBottomRef.current = false;
        return;
      }
      requestAnimationFrame(() => {
        isPinnedToBottomRef.current = checkIfPinnedToBottom();
      });
    };

    const onTouchOrPointer = (): void => {
      if (isProgrammaticScrollRef.current) return;
      requestAnimationFrame(() => {
        isPinnedToBottomRef.current = checkIfPinnedToBottom();
      });
    };

    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('touchstart', onTouchOrPointer, { passive: true });
    container.addEventListener('pointerdown', onTouchOrPointer, { passive: true });

    return () => {
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', onTouchOrPointer);
      container.removeEventListener('pointerdown', onTouchOrPointer);
    };
  }, [respectUserScroll, checkIfPinnedToBottom]);

  return {
    containerRef,
    contentRef,
    endRef,
    handleScroll,
    scrollToBottom,
    scrollToBottomIfNewMessages,
    scrollToBottomOnContentResize,
  };
}
